import { MoreIcon } from '@persona-id/design-tokens/icons';
import { ComponentProps, MouseEvent, ReactChild, ReactElement } from 'react';

import Button from 'design-system/components/Button';
import type { DropdownMenuProps } from 'design-system/components/DropdownMenu';
import DropdownMenu from 'design-system/components/DropdownMenu';

export interface Item {
  id: string;
  label: ReactChild;
  onClick?: (e?: MouseEvent) => void;
  hidden?: boolean;
  variant?: 'basic' | 'danger';
  'data-test'?: string;
}

interface ContextMenuProps
  extends Pick<ComponentProps<typeof DropdownMenu>, 'align' | 'side'>,
    Pick<ComponentProps<typeof Button>, 'size'> {
  id?: string;
  'data-test'?: string;
  items: Item[];
  renderItem: (item: Item) => ReactElement;
  contentProps?: DropdownMenuProps['contentProps'];
  disabled?: boolean;
  variant?: 'basic' | 'primary' | 'danger' | 'inverse';
  'aria-label'?: string;
  portal?: boolean;
}

const ContextMenu = ({
  id,
  'data-test': dataTest,
  items,
  renderItem,
  disabled,
  align,
  side,
  size,
  contentProps = {},
  variant = 'basic',
  'aria-label': ariaLabel = 'More options',
  portal,
}: ContextMenuProps) => {
  const filteredItems = items.filter((item) => !item.hidden);

  if (filteredItems.length === 0) {
    return null;
  }

  // Prevent Mouse click events propagating when clicking on the dropdown
  const combinedContentProps = {
    onClick: (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
    },
    // Needed to trigger onClick in Firefox
    onMouseDown: (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
    },
    ...contentProps,
  };

  return (
    <DropdownMenu
      id={`context-menu-dropdown__${id}`}
      trigger={
        <Button
          id={id}
          data-test={dataTest}
          width='icon'
          size={size}
          variant={variant}
          disabled={disabled}
          aria-label={ariaLabel}
          onClick={(e) => {
            // Prevent Mouse click events propagating when clicking on button
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <MoreIcon />
        </Button>
      }
      align={align}
      side={side}
      disabled={disabled}
      contentProps={combinedContentProps}
      portal={portal}
    >
      {filteredItems.map(renderItem)}
    </DropdownMenu>
  );
};

ContextMenu.Item = DropdownMenu.Item;

export default ContextMenu;
